<template>
  <v-card class="pb-2 px-2 rounded-20" width="550px">
    <v-card-title class="mb-1">
      <h2 class="title-h2">Confirm Order</h2>
    </v-card-title>
    <v-card-text class="pb-2">
      <p v-if="!currentTenant.activated" class="black--text">
        <strong> This transaction will activate your Subscription. </strong>
      </p>
      <p v-if="usingQuotaCredit">
        You have enough credits to complete this transaction!
      </p>

      <strong> Order Summary </strong>
      <div class="d-flex justify-space-between title-font mt-1">
        <small>Description</small>
        <small>Amount</small>
      </div>
      <v-divider class="mb-3" />
      <div class="d-flex justify-space-between title-font">
        <h4 class="black--text ml-3">{{ item.description }}</h4>
        <p class="mb-0" v-if="!usingPaymentMethod">
          {{ item.quantity }} {{ toTitleCase(item.product_code) }} Credit
        </p>
        <p class="mb-0" v-if="usingPaymentMethod">
          {{ formatStripeAmount(item.price * 100, item.currency) }}
          {{ item.currency }}
        </p>
      </div>
      <div
        v-for="(s, index) in (strategy || []).filter((s) => s.addon_for)"
        :key="index"
        class="d-flex justify-space-between title-font"
      >
        <h4 class="black--text ml-3">{{ toTitleCase(s.product_code) }}</h4>
        <p class="mb-0" v-if="!usingPaymentMethod">
          {{ item.quantity }} {{ toTitleCase(s.product_code) }} Credit
        </p>
        <p class="mb-0" v-if="usingPaymentMethod">
          {{ formatStripeAmount(s.price * 100, item.currency) }}
          {{ item.currency }}
        </p>
      </div>
      <div
        class="d-flex justify-space-between title-font"
        v-if="totalTaxAmount != null && !usingPostPay"
      >
        <h4 class="black--text ml-3">
          Sales Tax{{ taxInclusive ? " (included)" : "" }}
        </h4>
        <p class="mb-0">
          {{ formatStripeAmount(totalTaxAmount, item.currency) }}
          {{ item.currency }}
        </p>
      </div>
      <div
        class="d-flex justify-space-between title-font"
        v-if="customerBalance < 0 && usingPaymentMethod"
      >
        <h4 class="black--text ml-3">Available Balance</h4>
        <p class="mb-0">
          {{ formatStripeAmount(Math.abs(customerBalance), item.currency) }}
          {{ item.currency }}
        </p>
      </div>
      <div
        class="d-flex justify-space-between title-font"
        v-if="customerBalance < 0 && usingPaymentMethod"
      >
        <h4 class="black--text ml-3">Remaining Balance</h4>
        <p class="mb-0">
          {{
            formatStripeAmount(
              Math.max(0, Math.abs(customerBalance) - totalPriceCents),
              item.currency
            )
          }}
          {{ item.currency }}
        </p>
      </div>
      <div class="d-flex justify-space-between title-font mt-2">
        <small>Shipping Address</small>
      </div>
      <v-divider class="mb-3" />
      <div class="d-flex justify-space-between title-font">
        <h4
          class="black--text ml-3"
          v-if="tenantShippingAddresses.length === 1"
        >
          {{ shippingAddressText(tenantShippingAddresses[0]) }}
        </h4>
        <v-select
          v-if="tenantShippingAddresses.length > 1"
          v-model="selectedShippingAddressUid"
          label="Select Shipping Address"
          :items="shippingAddressSelections()"
          outlined
          dense
          hide-details
          :menu-props="{ rounded: '10' }"
          class="my-1"
        />
      </div>
      <v-divider class="my-3" />
      <div class="title-font text-right">
        <h3>Total Due:</h3>
        <p class="pricing-card-price mt-2 mb-4">
          <span v-if="usingQuotaCredit">
            {{ item.quantity }} {{ toTitleCase(item.product_code) }} Credit
          </span>
          <span v-if="usingPostPay">
            No Credits Due<span
              style="font-size: medium; vertical-align: text-top"
              >*</span
            >
          </span>
          <span v-if="usingPaymentMethod && item.tax_amount != null">
            {{
              formatStripeAmount(
                Math.max(0, totalPriceCents + customerBalance),
                item.currency
              )
            }}
            {{ item.currency }}
          </span>
          <span v-if="usingPaymentMethod && item.tax_amount == null">
            {{ formatStripeAmount(item.price * 100, item.currency) }}
          </span>
        </p>
        <p
          class="pricing-card-price mt-2 mb-4"
          v-if="item.tax_amount && usingQuotaCredit"
        >
          <span>
            + {{ formatStripeAmount(totalTaxAmount, item.currency) }}
            {{ item.currency }} Sales Tax
          </span>
        </p>
        <p v-if="usingPostPay">
          * This transaction will be charged to your post-paid account.
        </p>
      </div>
      <p v-if="usingPaymentMethod">
        <strong>Payment Summary</strong>
        <PaymentMethodTable
          :paymentMethods="chosenPaymentMethod ? [chosenPaymentMethod] : []"
          :loading="initialising"
        />
      </p>

      <div v-if="usingQuotaCredit" class="my-1 flex-column">
        <span class="d-block mb-1">
          {{ toTitleCase(item.product_code) }} Credits Remaining After
          Transaction - ( {{ quota.credits }} of
          {{ quota.starting + quota.manually_added }} )
        </span>
        <div
          class="flex-grow-1"
          :style="
            'display: grid; grid-template-columns: ' +
            `${quota.incoming}fr ` +
            `${quota.starting_rollover + quota.manually_added}fr;`
          "
        >
          <v-progress-linear
            :value="
              Math.floor(
                (Math.min(quota.incoming, quota.credits) / quota.incoming) * 100
              )
            "
          />
          <v-progress-linear
            color="success"
            :value="
              Math.floor(
                (Math.max(
                  0,
                  quota.starting_rollover +
                    quota.manually_added -
                    (quota.starting + quota.manually_added - quota.credits)
                ) /
                  (quota.starting_rollover + quota.manually_added)) *
                  100
              )
            "
          />
        </div>
      </div>
      <div v-if="!usingQuotaCredit">
        <v-card v-if="currentTenant.payment_terms === 'net_30'" flat>
          <v-card-title>
            <v-icon class="mr-2" color="black">mdi-clock</v-icon>
            No payment will be taken now
          </v-card-title>
          <v-card-text>
            Your account payment terms are 30 days.<br />
            Payment for this charge is due:
            <strong>{{ netTermsDueDate(30) }}</strong>
          </v-card-text>
        </v-card>
        <v-card v-if="currentTenant.payment_terms === 'net_60'" flat>
          <v-card-title>
            <v-icon class="mr-2" color="black">mdi-clock</v-icon>
            No payment will be taken now
          </v-card-title>
          <v-card-text>
            Your account payment terms are 60 days.<br />
            Payment for this charge is due:
            <strong>{{ netTermsDueDate(60) }}</strong>
          </v-card-text>
        </v-card>
      </div>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        v-if="usingPaymentMethod && paymentMethods && paymentMethods.length"
        class="mr-2 elevation-0 rounded-10"
        :disabled="loading || initialising"
        @click="paymentSelectDialog = true"
      >
        CHANGE PAYMENT METHOD
      </v-btn>
      <v-btn
        v-if="usingPaymentMethod && paymentMethods && !paymentMethods.length"
        class="mr-2 elevation-0 rounded-10"
        :disabled="loading || initialising"
        @click="addPaymentMethodDialog = true"
      >
        ADD A PAYMENT METHOD
      </v-btn>
      <v-btn
        class="btn-error"
        elevation="0"
        @click="dialog = false"
        :disabled="loading || initialising"
      >
        CANCEL
      </v-btn>
      <v-btn
        class="btn-primary"
        @click="emitConfirm"
        :disabled="initialising || (!chosenPaymentMethod && usingPaymentMethod)"
        :loading="loading"
      >
        CONFIRM {{ usingPaymentMethod ? "AND PAY" : null }}
      </v-btn>
    </v-card-actions>
    <v-dialog
      v-model="paymentSelectDialog"
      width="700px"
      content-class="rounded-20"
    >
      <v-card class="rounded-20">
        <v-card-title class="title-font">
          Select a Payment Method
        </v-card-title>
        <PaymentMethodTable
          :paymentMethods="paymentMethods"
          @select="selectPaymentMethod"
        />
      </v-card>
    </v-dialog>
    <AddPaymentMethodDialog
      v-model="addPaymentMethodDialog"
      :loading="loading"
      @error="onError"
    />
  </v-card>
</template>

<script>
import client from "@/lib/ApiClient"
import { mapActions, mapGetters } from "vuex"
import { toTitleCase, shippingAddressText, formatStripeAmount } from "@/utils"
import PaymentMethodTable from "@/components/shared/PaymentMethodTable.vue"
import AddPaymentMethodDialog from "@/components/shared/PaymentMethods/AddPaymentMethodDialog.vue"

export default {
  name: "PurchaseCard",

  props: [
    "value",
    "item",
    "strategy",
    "acquisitionMethod",
    "loading",
    "customerBalance",
    "totalPriceCents",
    "totalTaxAmount",
    "taxInclusive"
  ],

  components: { PaymentMethodTable, AddPaymentMethodDialog },

  data() {
    return {
      paymentSelectDialog: false,
      addPaymentMethodDialog: false,
      chosenPaymentMethod: null,
      initialising: true,
      selectedShippingAddressUid: null
    }
  },

  watch: {
    dialog(val) {
      if (val && this.paymentMethods?.length === 0 && this.usingPaymentMethod) {
        this.addPaymentMethodDialog = true
      }
    },

    paymentMethods(val, prevVal) {
      if (val && prevVal === null) {
        this.chosenPaymentMethod = val?.find((m) => m.default)
        this.initialising = false
      }
      if (val && prevVal?.length === 0) {
        this.chosenPaymentMethod = val?.find((m) => m.default)
      }
    },

    loading(val, prevVal) {
      if (!this.currentTenant.activated && val === false && prevVal === true) {
        this.setCurrentTenant({ ...this.currentTenant, activated: true })
        this.setSubscription({ ...this.subscription, activated: true })
      }
    },

    selectedShippingAddressUid(val, prevVal) {
      if (val && prevVal) {
        this.$emit("reloadAcquisitionStrategy", val)
      }
    },

    currentPlan(val, prevVal) {
      if (val && prevVal) {
        this.$emit("reloadAcquisitionStrategy", this.selectedShippingAddressUid)
      }
    }
  },

  computed: {
    ...mapGetters([
      "paymentMethods",
      "currentPlan",
      "currentQuotas",
      "currentTenant",
      "subscription",
      "tenantShippingAddresses"
    ]),

    dialog: {
      get() {
        return this.value
      },
      set(value) {
        return this.$emit("input", value)
      }
    },

    usingPaymentMethod() {
      return !this.usingQuotaCredit && !this.usingPostPay
    },

    usingPostPay() {
      const quota = this.currentQuotas.find(
        (q) => q.quota_code === this.item.quota_code
      )
      if (quota.current > 0) return false
      const planQuota = this.currentPlan.quotas.find(
        (q) => q.code === this.item.quota_code
      )
      return planQuota.post_pay && !this.usingQuotaCredit
    },

    usingQuotaCredit() {
      return this.acquisitionMethod === "quota"
    },

    quota() {
      if (this.usingPaymentMethod) return
      const quota = this.currentQuotas.find(
        (q) => q.quota_code === this.item.quota_code
      )

      return {
        credits: this.usingPostPay ? quota.current : quota.current - 1,
        starting: quota.starting,
        starting_rollover: quota.starting_rollover,
        manually_added: quota.manually_added,
        incoming: quota.incoming
      }
    }
  },

  methods: {
    ...mapActions([
      "setPaymentMethods",
      "setCurrentTenant",
      "setSubscription",
      "setErrorMessage"
    ]),

    toTitleCase,

    shippingAddressText,

    formatStripeAmount,

    initialise() {
      this.chosenPaymentMethod = this.paymentMethods?.find((m) => m.default)
      this.initialising = false
    },

    selectPaymentMethod(paymentMethod) {
      this.chosenPaymentMethod = paymentMethod
      this.paymentSelectDialog = false
    },

    emitConfirm() {
      this.$emit(
        "confirm",
        this.chosenPaymentMethod?.payment_id,
        this.selectedShippingAddressUid
      )
    },

    shippingAddressSelections() {
      return this.tenantShippingAddresses.map((addr) => {
        return {
          text: this.shippingAddressText(addr),
          value: addr.uid
        }
      })
    },

    onError(message) {
      this.setErrorMessage(message)
    },

    netTermsDueDate(days) {
      const today = new Date()
      const futureDate = new Date()
      futureDate.setDate(today.getDate() + days)
      return futureDate.toLocaleDateString()
    }
  },

  async mounted() {
    if (this.paymentMethods?.length) {
      this.chosenPaymentMethod = this.paymentMethods?.find((m) => m.default)
    } else {
      this.setPaymentMethods(await client.gateway.billing.fetchPaymentMethods())
    }
    if (
      this.usingPaymentMethod &&
      this.dialog &&
      this.paymentMethods?.length === 0
    ) {
      this.addPaymentMethodDialog = true
    }
    if (this.tenantShippingAddresses.length === 1)
      this.selectedShippingAddressUid = this.tenantShippingAddresses[0].uid
    else
      this.selectedShippingAddressUid = this.tenantShippingAddresses.find(
        (addr) => addr.uid === this.currentTenant.primary_shipping_address_uid
      ).uid

    this.initialising = false
  }
}
</script>
